/* eslint-disable react/prop-types */
/**
 * Trust Center primitives — Phase A.
 * Small, low-risk, framework-free components that every TC surface consumes
 * so the design language stays cohesive as the admin and public pages are
 * refined. No behavior change; nothing outside the Trust Center should
 * import from this file.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, Info, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';

// ────────────────────────────────────────────────────────────────────────────
// TCBadge — one unified badge for status / framework / category / access
// ────────────────────────────────────────────────────────────────────────────
const BADGE_TONES = {
  neutral: 'bg-[var(--tc-surface-3)] text-[var(--tc-mute)] border-[var(--tc-line)]',
  info:    'bg-[var(--tc-blue-soft)] text-[var(--tc-blue)] border-transparent',
  success: 'bg-[var(--tc-success-soft)] text-[var(--tc-success)] border-transparent',
  warn:    'bg-[var(--tc-warn-soft)] text-[var(--tc-warn)] border-transparent',
  danger:  'bg-[var(--tc-danger-soft)] text-[var(--tc-danger)] border-transparent',
  indigo:  'bg-[var(--tc-indigo-soft)] text-[var(--tc-indigo)] border-transparent',
  outline: 'bg-[var(--tc-surface)] text-[var(--tc-mute)] border-[var(--tc-line)]',
};
const BADGE_SIZES = {
  sm: 'text-[10px] leading-4 px-1.5 py-[1px] rounded-full',
  md: 'text-[11px] leading-4 px-2 py-0.5 rounded-full',
};

export const TCBadge = ({ tone = 'neutral', size = 'sm', icon, children, className = '', 'data-testid': testid, ...rest }) => (
  <span
    data-testid={testid}
    className={`inline-flex items-center gap-1 font-medium border ${BADGE_TONES[tone] || BADGE_TONES.neutral} ${BADGE_SIZES[size] || BADGE_SIZES.sm} ${className}`}
    {...rest}
  >
    {icon ? <span className="inline-flex items-center [&>svg]:w-3 [&>svg]:h-3">{icon}</span> : null}
    {children}
  </span>
);

// ────────────────────────────────────────────────────────────────────────────
// Callout — light, non-modal advisory panel. Replaces heavy `border-l-4`
// coloured blocks currently used for stale-policy warnings, drafts notice, etc.
// ────────────────────────────────────────────────────────────────────────────
const CALLOUT_TONES = {
  info:    { bg: 'bg-[var(--tc-blue-soft)]',    text: 'text-[var(--tc-blue)]',    icon: Info },
  warn:    { bg: 'bg-[var(--tc-warn-soft)]',    text: 'text-[var(--tc-warn)]',    icon: AlertTriangle },
  success: { bg: 'bg-[var(--tc-success-soft)]', text: 'text-[var(--tc-success)]', icon: CheckCircle2 },
  danger:  { bg: 'bg-[var(--tc-danger-soft)]',  text: 'text-[var(--tc-danger)]',  icon: XCircle },
};
export const Callout = ({ tone = 'info', title, children, action, className = '', 'data-testid': testid }) => {
  const t = CALLOUT_TONES[tone] || CALLOUT_TONES.info;
  const Icon = t.icon;
  return (
    <div data-testid={testid} className={`${t.bg} rounded-[var(--tc-radius)] px-3 py-2.5 flex items-start gap-2.5 ${className}`}>
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${t.text}`} />
      <div className="min-w-0 flex-1">
        {title ? <div className={`text-[13px] font-semibold ${t.text} leading-5`}>{title}</div> : null}
        {children ? <div className="text-[12px] leading-[18px] text-[var(--tc-ink-2)] mt-0.5">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// EmptyState — one shape for every empty tab / table / filter result
// ────────────────────────────────────────────────────────────────────────────
export const EmptyState = ({ icon, title, description, cta, secondaryCta, size = 'md', className = '', 'data-testid': testid }) => {
  const pad = size === 'sm' ? 'py-8' : size === 'lg' ? 'py-16' : 'py-12';
  return (
    <div data-testid={testid} className={`text-center ${pad} px-6 ${className}`}>
      {icon ? <div className="w-10 h-10 mx-auto mb-3 text-[var(--tc-mute-soft)] flex items-center justify-center">{icon}</div> : null}
      {title ? <div className="tc-h2 mb-1">{title}</div> : null}
      {description ? <div className="tc-muted max-w-md mx-auto">{description}</div> : null}
      {(cta || secondaryCta) ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          {cta}
          {secondaryCta}
        </div>
      ) : null}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// PaginationFooter — enterprise pagination for server-side lists.
// Shared markup so Resources / Policies / Requests / Updates all look
// identical. Accepts already-computed values from the parent — no data
// fetching here.
// ────────────────────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PageBtn = ({ children, disabled, active, onClick, testid }) => (
  <button
    type="button" onClick={onClick} disabled={disabled}
    data-testid={testid}
    aria-current={active ? 'page' : undefined}
    className={[
      'inline-flex items-center justify-center h-7 min-w-7 px-2 rounded-[var(--tc-radius-sm)] text-[12px] font-medium border transition-colors tc-focus',
      active
        ? 'bg-[var(--tc-blue)] text-white border-[var(--tc-blue)]'
        : 'bg-[var(--tc-surface)] text-[var(--tc-ink-2)] border-[var(--tc-line)] hover:bg-[var(--tc-surface-3)]',
      disabled ? 'opacity-40 cursor-not-allowed hover:bg-[var(--tc-surface)]' : '',
    ].join(' ')}
  >
    {children}
  </button>
);

export const PaginationFooter = ({
  page, pageSize, total, onPageChange, onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className = '', testidPrefix = 'tc-paging',
}) => {
  const p = Math.max(1, parseInt(page, 10) || 1);
  const size = Math.max(1, parseInt(pageSize, 10) || 25);
  const totalPages = Math.max(1, Math.ceil((total || 0) / size));
  const from = total === 0 ? 0 : (p - 1) * size + 1;
  const to = Math.min(total || 0, p * size);

  // Compact page-number window: first, last, current ± 1
  const nums = [];
  const push = (n) => { if (!nums.includes(n) && n >= 1 && n <= totalPages) nums.push(n); };
  push(1); push(p - 1); push(p); push(p + 1); push(totalPages);
  nums.sort((a, b) => a - b);

  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 border-t border-[var(--tc-line)] ${className}`} data-testid={`${testidPrefix}-footer`}>
      <div className="flex items-center gap-2 text-[12px] text-[var(--tc-mute)]" data-testid={`${testidPrefix}-summary`}>
        <span>Showing <span className="tabular-nums text-[var(--tc-ink-2)] font-medium">{from}</span>–<span className="tabular-nums text-[var(--tc-ink-2)] font-medium">{to}</span> of <span className="tabular-nums text-[var(--tc-ink-2)] font-medium">{total || 0}</span></span>
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-[12px] text-[var(--tc-mute)]">
          <span>Rows per page</span>
          <select
            value={size}
            onChange={(e) => onPageSizeChange && onPageSizeChange(parseInt(e.target.value, 10))}
            className="h-7 rounded-[var(--tc-radius-sm)] border border-[var(--tc-line)] bg-[var(--tc-surface)] text-[12px] text-[var(--tc-ink-2)] px-1.5 tc-focus"
            data-testid={`${testidPrefix}-page-size`}
          >
            {pageSizeOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <div className="flex items-center gap-1">
          <PageBtn disabled={p <= 1} onClick={() => onPageChange && onPageChange(p - 1)} testid={`${testidPrefix}-prev`}>
            <ChevronLeft className="w-3.5 h-3.5" />
          </PageBtn>
          {nums.map((n, idx) => (
            <React.Fragment key={n}>
              {idx > 0 && n - nums[idx - 1] > 1 ? <span className="text-[12px] text-[var(--tc-mute-soft)] px-1">…</span> : null}
              <PageBtn active={n === p} onClick={() => n !== p && onPageChange && onPageChange(n)} testid={`${testidPrefix}-page-${n}`}>{n}</PageBtn>
            </React.Fragment>
          ))}
          <PageBtn disabled={p >= totalPages} onClick={() => onPageChange && onPageChange(p + 1)} testid={`${testidPrefix}-next`}>
            <ChevronRight className="w-3.5 h-3.5" />
          </PageBtn>
        </div>
      </div>
    </div>
  );
};
